import React, { useState, useEffect } from "react";
import { StudyMaterial } from "../../../../types/dataModel";
import { getAPIHeaders } from "../../../../common/Utils";
import useFetch from "../../../../hooks/useFetch";
import { API } from "../../../../common/Constants";
import FormFooter from "../../../../core/common/formFooter";
import PreLoader from "../../../../core/common/preLoader";
import CustomToast from "../../../../core/common/customToast";
import VideoMaterialFormSection from "./videoMaterialFormSection";
import FileMaterialFormSection from "./fileMaterialFormSection";

interface Props {
  syllabusId: number;
  studyMaterial?: StudyMaterial | null;
  modalData: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const StudyMaterialForm = ({ syllabusId, studyMaterial, modalData, onSuccess, onCancel }: Props) => {
  const [formData, setFormData] = useState<Partial<StudyMaterial>>({
    title: "",
    content: "",
    materialType: "NOTE",
    public: true,
    files: [],
  });
  const [nodeData, setNodeData] = useState<any>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "Something went wrong please try again later",
    heading: "Oops! Something went wrong",
    type: "ERROR",
  });

  const { isLoading, error, callFetch } = useFetch();

  useEffect(() => {
    if (error?.response?.data) {
      setToastSettings((prev) => ({
        ...prev,
        show: true,
        message: error.response?.data || "Something went wrong, please try again later.",
        heading: "Error",
        type: "ERROR",
      }));
    }
  }, [error]);

  useEffect(() => {
    if (studyMaterial) {
      setFormData(studyMaterial);
    } else {
      setFormData({
        title: "",
        content: "",
        materialType: "NOTE",
        public: true,
        files: [],
      });
    }
  }, [studyMaterial]);

  useEffect(() => {
    if (modalData) {
      setNodeData(modalData);
    }
  }, [modalData]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Title is required";
    }

    if (formData.materialType === "NOTE" && !formData.content?.trim()) {
      newErrors.content = "Note content is required";
    }

    if (formData.materialType === "LINK") {
      if (!formData.content?.trim()) {
        newErrors.content = "Link URL is required";
      } else if (!isValidUrl(formData.content)) {
        newErrors.content = "Please enter a valid URL";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string) => {
    try {
      const urlToCheck = url.startsWith("http") ? url : `https://${url}`;
      new URL(urlToCheck);
      return true;
    } catch (e) {
      return false;
    }
  };


  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (formData.materialType === "VIDEO" || formData.materialType === "FILE") {
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      if (studyMaterial?.id) {
        await callFetch(
          API.UPDATE_STUDY_MATERIAL.replace(":id", studyMaterial.id.toString()),
          "PUT",
          getAPIHeaders(),
          null,
          { ...formData, isPublic: formData.public || false }
        );

        onSuccess();
      } else {
        callFetch(
          API.ADD_STUDY_MATERIAL,
          "POST",
          getAPIHeaders(),
          {
            syllabusId: syllabusId,
            unitId: nodeData?.nodeType === "UNIT" ? nodeData.id : null,
            sectionId: nodeData?.nodeType === "SECTION" ? nodeData.id : null,
            title: formData.title,
            content: formData.content,
            materialType: formData.materialType,
            isPublic: formData.public || false,
          },
          null
        )
          .then(() => {
            onSuccess();
          })
          .catch(() => {});
      }
    } catch (error) {
      console.error("Error saving study material:", error);
    }
  };

  const rendermaterialTypeTabs = () => (
    <div className="d-flex border-bottom mb-2">
      <div className="mb-2 w-100">
        <button
          type="button"
          disabled={studyMaterial?.id !== undefined && studyMaterial?.materialType !== "NOTE"}
          className={`btn btn-sm ${formData.materialType === "NOTE" ? "btn-primary" : "btn-outline-primary"} me-2`}
          onClick={() => setFormData((prev) => ({ ...prev, materialType: "NOTE" }))}
        >
          <i className="ti ti-file-text me-1" />
          Note
        </button>
        <button
          type="button"
          disabled={studyMaterial?.id !== undefined && studyMaterial?.materialType !== "FILE"}
          className={`btn btn-sm ${formData.materialType === "FILE" ? "btn-primary" : "btn-outline-primary"} me-2`}
          onClick={() => setFormData((prev) => ({ ...prev, materialType: "FILE" }))}
        >
          <i className="ti ti-file me-1" />
          File
        </button>
        <button
          type="button"
          disabled={studyMaterial?.id !== undefined && studyMaterial?.materialType !== "LINK"}
          className={`btn btn-sm ${formData.materialType === "LINK" ? "btn-primary" : "btn-outline-primary"} me-2`}
          onClick={() => setFormData((prev) => ({ ...prev, materialType: "LINK" }))}
        >
          <i className="ti ti-link me-1" />
          Link
        </button>
        <button
          type="button"
          disabled={studyMaterial?.id !== undefined && studyMaterial?.materialType !== "VIDEO"}
          className={`btn btn-sm ${formData.materialType === "VIDEO" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setFormData((prev) => ({ ...prev, materialType: "VIDEO" }))}
        >
          <i className="ti ti-video me-1" />
          Video
        </button>
      </div>
    </div>
  );

  const renderNoteForm = () => (
    <div className="mb-3">
      <div className="mb-3">
        <label className="form-label">Title</label>
        <input
          type="text"
          className={`form-control ${errors.title ? "is-invalid" : ""}`}
          placeholder="Enter material title..."
          value={formData.title || ""}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, title: e.target.value }));
            if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
          }}
        />
        {errors.title && <div className="invalid-feedback">{errors.title}</div>}
      </div>
      <label className="form-label">Note Content</label>
      <textarea
        className={`form-control ${errors.content ? "is-invalid" : ""}`}
        rows={6}
        placeholder="Enter your note content here..."
        value={formData?.content}
        onChange={(e) => {
          setFormData((prev) => ({ ...prev, content: e.target.value }));
          if (errors.content) setErrors((prev) => ({ ...prev, content: "" }));
        }}
      />
      {errors.content && <div className="invalid-feedback">{errors.content}</div>}
    </div>
  );

  const renderLinkForm = () => (
    <div className="mb-3">
      <div className="mb-3">
        <label className="form-label">Title</label>
        <input
          type="text"
          className={`form-control ${errors.title ? "is-invalid" : ""}`}
          placeholder="Enter material title..."
          value={formData.title || ""}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, title: e.target.value }));
            if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
          }}
        />
        {errors.title && <div className="invalid-feedback">{errors.title}</div>}
      </div>
      <label className="form-label">Link URL</label>
      <input
        type="url"
        className={`form-control ${errors.content ? "is-invalid" : ""}`}
        placeholder="https://example.com or youtube.com/watch?v=..."
        value={formData.content || ""}
        onChange={(e) => {
          setFormData((prev) => ({ ...prev, content: e.target.value }));
          if (errors.content) setErrors((prev) => ({ ...prev, content: "" }));
        }}
      />
      {errors.content && <div className="invalid-feedback">{errors.content}</div>}
      <div className="form-text">Enter a valid URL. For YouTube videos, you can use the share link.</div>
    </div>
  );

  const renderFileSection = () => (
    <FileMaterialFormSection
      syllabusId={syllabusId}
      studyMaterial={studyMaterial}
      nodeData={nodeData}
      isPublic={formData.public || false}
      onIsPublicChange={(value) => setFormData((prev) => ({ ...prev, public: value }))}
      onSuccess={onSuccess}
      onCancel={onCancel}
    />
  );

  const renderVisibilityToggle = () => (
    <div className="mb-3">
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          id="isPublic"
          checked={formData?.public || false}
          onChange={(e) => setFormData((prev) => ({ ...prev, public: e.target.checked }))}
        />
        <label className="form-check-label" htmlFor="isPublic">
          Make this material public
        </label>
      </div>
      <div className="form-text">Public materials are visible to all students. Private materials are only visible to faculty.</div>
    </div>
  );

  const renderVideoSection = () => (
    <VideoMaterialFormSection
      syllabusId={syllabusId}
      studyMaterial={studyMaterial}
      nodeData={nodeData}
      isPublic={formData.public || false}
      onIsPublicChange={(value) => setFormData((prev) => ({ ...prev, public: value }))}
      onSuccess={onSuccess}
      onCancel={onCancel}
    />
  );

  const renderBody = () => {
    if (formData.materialType === "NOTE") {
      return renderNoteForm();
    }

    if (formData.materialType === "LINK") {
      return renderLinkForm();
    }

    return null;
  };

  return (
    <>
      <PreLoader showLoader={isLoading} />
      <CustomToast
        show={toastSettings.show}
        message={toastSettings.message}
        heading={toastSettings.heading}
        type={toastSettings.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />

      {formData.materialType === "VIDEO" ? (
        <>
          {rendermaterialTypeTabs()}
          {renderVideoSection()}
        </>
      ) : formData.materialType === "FILE" ? (
        <>
          {rendermaterialTypeTabs()}
          {renderFileSection()}
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          {rendermaterialTypeTabs()}

          {renderBody()}

          {renderVisibilityToggle()}

          <FormFooter onCancel={onCancel} onSave={handleSubmit} readonly={isLoading} noBootstrap={true} />
        </form>
      )}
    </>
  );
};

export default StudyMaterialForm;
